import type { UpdateCustomerInput } from '../types/customers.types';
import type { DiffEntry } from '../components/EditCustomerForm/computeCustomerDiff';

/** The eight editable contact/address/company fields, in all three spellings
 *  this feature has to reconcile: the form's, the wire's, and the diff's. */
const EDITABLE_FIELDS = [
    'addressLine1',
    'addressLine2',
    'city',
    'postcode',
    'email',
    'mobilePhone',
    'businessName',
    'vatNumber',
] as const;

export type EditableHolderField = (typeof EDITABLE_FIELDS)[number];

const WIRE_NAMES: Record<EditableHolderField, string> = {
    addressLine1: 'address_line_1',
    addressLine2: 'address_line_2',
    city: 'city',
    postcode: 'postcode',
    email: 'email',
    mobilePhone: 'mobile_phone',
    businessName: 'business_name',
    vatNumber: 'vat_number',
};

const isEditableField = (field: string): field is EditableHolderField =>
    (EDITABLE_FIELDS as readonly string[]).includes(field);

/** Turns the confirmed diff into the partial payload the mutation sends, so
 *  only the fields the agent actually changed travel. */
export function diffToUpdateInput(diff: DiffEntry[]): UpdateCustomerInput {
    const input: UpdateCustomerInput = {};

    diff.forEach((entry) => {
        if (isEditableField(entry.field)) {
            input[entry.field] = entry.newValue;
        }
    });

    return input;
}

/** Maps a server field name from a 422 back onto the form field that owns it.
 *  Returns undefined for a field this form does not render. */
export function wireFieldToFormField(wireField: string): EditableHolderField | undefined {
    return EDITABLE_FIELDS.find((field) => WIRE_NAMES[field] === wireField);
}
